/**
 * BackendClient over the Dropstack HTTP API (contract: ./api-types.ts).
 *
 * Resilience rules — Play must never block on the network:
 * - Every request has a 4 s timeout (AbortController).
 * - startSession() falls back to an offline ticket (offline: true, token '')
 *   when the server can't issue one; such runs are playable but unranked.
 * - submitRun() with an offline ticket answers { accepted: false, reason:
 *   'offline' } without touching the network.
 * - The last profile and saved rating are mirrored locally, so an offline
 *   ticket still warm-starts near the player's level.
 *
 * Credentials `{ playerId, secret }` are stored under "dropstack.auth" and sent
 * as `Authorization: Bearer <playerId>.<secret>`.
 */
import { RULES_VERSION, type Locale, type RunSummary } from '@dropstack/core';
import type {
  BackendClient,
  LeaderboardPage,
  LeaderboardQuery,
  PlayerProfile,
  ProfileInput,
  SchoolOption,
  SessionTicket,
  StartSessionOptions,
  SubmitResult,
} from '../../contracts.ts';
import { getProgressStore, localRatings, type ProgressStore } from '../../state/progress.ts';
import { loadSettings } from '../../state/settings.ts';
import { getAppStorage, isRecord, STORAGE_KEYS, type JsonStore } from '../../state/storage.ts';
import { searchCities } from '../geo.ts';
import { validateProfileInput } from '../profileValidation.ts';
import { randomId, randomSeed } from '../random.ts';
import {
  API_ROUTES,
  bearerToken,
  CITY_RESULTS_MAX,
  leaderboardSearchParams,
  routePath,
  type CreateAnonymousPlayerRequest,
  type CreateSessionRequest,
  type HealthResponse,
  type RouteDef,
  type SubmitRunRequest,
} from './api-types.ts';
import {
  decodeAnonymousPlayer,
  decodeCities,
  decodeErrorBody,
  decodeHealth,
  decodeLeaderboard,
  decodeMe,
  decodeProfile,
  decodeSchools,
  decodeSession,
  decodeSubmitResult,
  decodeUpdateMe,
} from './decode.ts';
import { BackendError } from './errors.ts';

/** Client identifier sent with submitted runs (diagnostics only). */
export const CLIENT_VERSION = 'web/0.1.0';
export const REQUEST_TIMEOUT_MS = 4000;

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface ApiBackendOptions {
  /** Prefix for API paths; '' = same origin (Vite proxies /api in dev). */
  baseUrl?: string;
  fetch?: FetchLike;
  store?: JsonStore;
  progress?: ProgressStore;
  timeoutMs?: number;
  now?: () => number;
  /** Seed source for offline tickets. */
  seed?: () => number;
  /** Language sent when creating an anonymous player (default: saved settings). */
  locale?: () => Locale;
  clientVersion?: string;
}

interface StoredAuth {
  playerId: string;
  secret: string;
}

interface RequestOptions {
  params?: Record<string, string>;
  query?: URLSearchParams;
  body?: unknown;
  /** Attach the Authorization header when credentials exist. */
  auth?: boolean;
  timeoutMs?: number;
}

/** The default fetch, called unbound-safe (window.fetch throws "Illegal invocation" when detached). */
const globalFetch: FetchLike = (input, init) => fetch(input, init);

export class ApiBackend implements BackendClient {
  readonly kind = 'server' as const;

  private readonly baseUrl: string;
  private readonly fetchImpl: FetchLike;
  private readonly store: JsonStore;
  private readonly progress: ProgressStore;
  private readonly timeoutMs: number;
  private readonly now: () => number;
  private readonly seed: () => number;
  private readonly locale: () => Locale;
  private readonly clientVersion: string;
  /** In-flight ensurePlayer(), so concurrent callers (React StrictMode) don't create two players. */
  private pendingPlayer: Promise<PlayerProfile> | null = null;

  constructor(options: ApiBackendOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
    this.fetchImpl = options.fetch ?? globalFetch;
    this.store = options.store ?? getAppStorage();
    this.progress = options.progress ?? getProgressStore();
    this.timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
    this.now = options.now ?? Date.now;
    this.seed = options.seed ?? randomSeed;
    this.locale = options.locale ?? (() => loadSettings(this.store).locale);
    this.clientVersion = options.clientVersion ?? CLIENT_VERSION;
  }

  /**
   * The player behind the stored credentials, creating an anonymous one when
   * there are none (or the server no longer knows them).
   * @throws BackendError when the server can't be reached and no profile is cached.
   */
  ensurePlayer(): Promise<PlayerProfile> {
    this.pendingPlayer ??= this.loadOrCreatePlayer().finally(() => {
      this.pendingPlayer = null;
    });
    return this.pendingPlayer;
  }

  /**
   * Validate locally (same rules as the server), then PATCH the profile.
   * @throws BackendError('invalid-input', 'invalid-profile') before sending, or the server's error.
   */
  async saveProfile(input: ProfileInput): Promise<PlayerProfile> {
    const checked = validateProfileInput(input);
    if (!checked.ok) throw new BackendError('invalid-input', 'invalid-profile', 'Profile is not valid', null, checked.problems);
    const { profile } = decodeUpdateMe(
      await this.withAuth(() => this.request(API_ROUTES.updateMe, { body: checked.value, auth: true })),
    );
    this.cacheProfile(profile);
    return profile;
  }

  async searchSchools(q: string, filter: { country?: string; province?: string; city?: string }): Promise<SchoolOption[]> {
    const query = new URLSearchParams();
    if (q.trim()) query.set('q', q.trim());
    if (filter.country) query.set('country', filter.country);
    if (filter.province) query.set('province', filter.province);
    if (filter.city) query.set('city', filter.city);
    return decodeSchools(await this.request(API_ROUTES.searchSchools, { query })).schools;
  }

  /** Server suggestions; falls back to the built-in town list when the server is unreachable. */
  async suggestCities(q: string, filter: { country: string; province?: string }): Promise<string[]> {
    const query = new URLSearchParams({ country: filter.country });
    if (filter.province) query.set('province', filter.province);
    if (q.trim()) query.set('q', q.trim());
    try {
      return decodeCities(await this.request(API_ROUTES.suggestCities, { query })).cities;
    } catch (err) {
      if (err instanceof BackendError && err.isUnreachable) return searchCities(q, filter, CITY_RESULTS_MAX);
      throw err;
    }
  }

  /**
   * A server-signed ticket, or an offline ticket (offline: true, token '')
   * when the server can't issue one — Play never blocks on the network.
   */
  async startSession(options: StartSessionOptions = {}): Promise<SessionTicket> {
    const relaxed = options.relaxed === true;
    // Standard runs send {} (servers that predate "More time" accept only that).
    const body: CreateSessionRequest = relaxed ? { relaxed: true } : {};
    try {
      const issuedAt = this.now();
      const session = decodeSession(await this.withAuth(() => this.request(API_ROUTES.createSession, { body, auth: true })));
      // The ticket carries the pace the server granted: that is the pace it replays at.
      return { ...session, issuedAt, offline: false };
    } catch (err) {
      if (err instanceof BackendError) return this.offlineTicket(relaxed);
      throw err;
    }
  }

  /**
   * Submit a finished run. Offline tickets and unreachable servers resolve to
   * `accepted: false` (reason 'offline' / 'network') and keep the rating
   * locally; server refusals (session used/expired/unknown) resolve with that
   * code as the reason. Never rejects on network errors.
   */
  async submitRun(ticket: SessionTicket, summary: RunSummary): Promise<SubmitResult> {
    if (ticket.offline || !ticket.token) return this.keepLocally(summary, 'offline', ticket.relaxed);
    const body: SubmitRunRequest = {
      token: ticket.token,
      events: summary.events,
      claimed: { raw: summary.raw, rated: summary.rated },
      clientVersion: this.clientVersion,
    };
    try {
      const result = decodeSubmitResult(
        await this.request(API_ROUTES.submitRun, { params: { id: ticket.sessionId }, body, auth: true }),
      );
      if (result.accepted) this.progress.recordRating(result.savedRating);
      return result;
    } catch (err) {
      if (!(err instanceof BackendError)) throw err;
      if (err.isUnreachable) return this.keepLocally(summary, 'network', ticket.relaxed);
      return {
        ...notAccepted(summary, err.code, ticket.relaxed),
        savedRating: this.progress.get().savedRating ?? 0,
      };
    }
  }

  async getLeaderboard(q: LeaderboardQuery): Promise<LeaderboardPage> {
    return decodeLeaderboard(await this.request(API_ROUTES.leaderboard, { query: leaderboardSearchParams(q), auth: true }));
  }

  // ───────────── players & auth ─────────────

  private async loadOrCreatePlayer(): Promise<PlayerProfile> {
    const auth = this.readAuth();
    if (auth) {
      try {
        const me = decodeMe(await this.request(API_ROUTES.getMe, { auth: true }));
        this.cacheProfile(me.profile);
        if (me.savedRating !== null) this.progress.recordRating(me.savedRating);
        return me.profile;
      } catch (err) {
        if (!(err instanceof BackendError)) throw err;
        if (err.status !== 401 && err.status !== 404) {
          const cached = this.cachedProfile();
          if (err.isUnreachable && cached?.playerId === auth.playerId) return cached;
          throw err;
        }
        this.clearAuth(); // unknown credentials: fall through and create a new player
      }
    }
    const request: CreateAnonymousPlayerRequest = { locale: this.locale() };
    const created = decodeAnonymousPlayer(await this.request(API_ROUTES.createAnonymousPlayer, { body: request }));
    this.store.set(STORAGE_KEYS.auth, { playerId: created.playerId, secret: created.secret } satisfies StoredAuth);
    this.cacheProfile(created.profile);
    return created.profile;
  }

  /**
   * Run an authenticated request, registering an anonymous player first when
   * there are no credentials. If the server answers 401 (it no longer knows
   * these credentials, e.g. after a database reset), start over once as a new
   * anonymous player and retry.
   */
  private async withAuth<T>(send: () => Promise<T>): Promise<T> {
    if (!this.readAuth()) await this.ensurePlayer();
    try {
      return await send();
    } catch (err) {
      if (!(err instanceof BackendError) || err.status !== 401) throw err;
      this.clearAuth();
      await this.ensurePlayer();
      return send();
    }
  }

  private readAuth(): StoredAuth | null {
    return this.store.get(STORAGE_KEYS.auth, (raw) =>
      isRecord(raw) && typeof raw.playerId === 'string' && typeof raw.secret === 'string' && raw.playerId && raw.secret
        ? { playerId: raw.playerId, secret: raw.secret }
        : null,
    );
  }

  private clearAuth(): void {
    this.store.remove(STORAGE_KEYS.auth);
    this.store.remove(STORAGE_KEYS.profileCache);
  }

  private cacheProfile(profile: PlayerProfile): void {
    this.store.set(STORAGE_KEYS.profileCache, profile);
  }

  private cachedProfile(): PlayerProfile | null {
    return this.store.get(STORAGE_KEYS.profileCache, decodeProfile);
  }

  // ───────────── sessions ─────────────

  /** A ticket for an unranked local run, warm-started from the locally mirrored rating. */
  private offlineTicket(relaxed: boolean): SessionTicket {
    const { startRating, referenceRating } = localRatings(this.progress.get());
    return {
      sessionId: randomId('offline'),
      token: '',
      seed: this.seed() >>> 0,
      startRating,
      referenceRating,
      rulesVersion: RULES_VERSION,
      issuedAt: this.now(),
      offline: true,
      relaxed,
    };
  }

  /** An unranked run: keep its rating locally so the next offline run starts near it. */
  private keepLocally(summary: RunSummary, reason: 'offline' | 'network', relaxed: boolean): SubmitResult {
    this.progress.recordRating(summary.savedRating);
    return { ...notAccepted(summary, reason, relaxed), savedRating: summary.savedRating };
  }

  // ───────────── HTTP ─────────────

  /** One JSON request with timeout and error mapping. Resolves to the parsed body (null when empty). */
  private async request(route: RouteDef, options: RequestOptions = {}): Promise<unknown> {
    const qs = options.query?.toString();
    const url = `${this.baseUrl}${routePath(route, options.params)}${qs ? `?${qs}` : ''}`;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.auth) {
      const auth = this.readAuth();
      if (auth) headers.Authorization = bearerToken(auth.playerId, auth.secret);
    }
    const init: RequestInit = { method: route.method, headers };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    return requestJson(this.fetchImpl, url, init, options.timeoutMs ?? this.timeoutMs);
  }
}

/**
 * fetch + timeout + JSON parse + ApiErrorBody mapping, shared with the health
 * probe. The timeout covers reading the body too.
 */
export async function requestJson(fetchImpl: FetchLike, url: string, init: RequestInit, timeoutMs: number): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let res: Response;
    let text: string;
    try {
      res = await fetchImpl(url, { ...init, signal: controller.signal });
      text = await res.text();
    } catch (err) {
      if (controller.signal.aborted) throw new BackendError('timeout', 'timeout', `No answer from ${url} within ${timeoutMs} ms`);
      throw new BackendError('network', 'network', `Could not reach ${url}: ${err instanceof Error ? err.message : String(err)}`);
    }
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text) as unknown;
      } catch {
        if (res.ok) throw new BackendError('invalid-response', 'invalid-response', `${url} did not return JSON`);
      }
    }
    if (!res.ok) {
      const e = decodeErrorBody(body);
      throw new BackendError('http', e.code, e.message || `HTTP ${res.status} from ${url}`, res.status, e.details);
    }
    return body;
  } finally {
    clearTimeout(timer);
  }
}

/** GET /api/health within `timeoutMs`; null when the server is unreachable or unhealthy. */
export async function probeServer(options: {
  baseUrl?: string | undefined;
  fetch?: FetchLike | undefined;
  timeoutMs: number;
}): Promise<HealthResponse | null> {
  const url = `${(options.baseUrl ?? '').replace(/\/+$/, '')}${API_ROUTES.health.path}`;
  const init: RequestInit = { method: 'GET', headers: { Accept: 'application/json' } };
  try {
    const health = decodeHealth(await requestJson(options.fetch ?? globalFetch, url, init, options.timeoutMs));
    return health.ok ? health : null;
  } catch (err) {
    if (err instanceof BackendError) return null;
    throw err;
  }
}

function notAccepted(summary: RunSummary, reason: string, relaxed: boolean): SubmitResult {
  return {
    accepted: false,
    flagged: false,
    runId: null,
    raw: summary.raw,
    rated: summary.rated,
    savedRating: summary.savedRating,
    ranks: {},
    personalBest: false,
    placement: false,
    relaxed,
    reason,
  };
}
